/**
 * Mic Pack Swap Algorithm Utilities
 * 
 * Derives actor scene presence from characters and generates swap suggestions
 * when the number of mic packs is less than the number of actors.
 */

import { MicAssignment, MicSwap } from './types';
import { Character, Act, Scene } from '../show-structure/types';
import { Personnel } from '../personnel/types';
import { CastAssignment } from '../personnel/types';
import { getPrimaryActorId } from '../personnel/utils/cast-assignment-selectors';
import { getOrderedSceneIds } from '../show-structure/utils/scene-order';

/**
 * Returns scene IDs in show order (Act 1 Scene 1 first, last scene last).
 */
export function getScenesInOrder(acts: Act[], scenes: Scene[]): string[] {
    return getOrderedSceneIds(acts, scenes);
}

/**
 * Builds a map of actor ID → set of scene IDs where the actor is present.
 * An actor's presence is the union of sceneIds from ALL their assigned characters.
 * Only includes actors (Personnel) who have at least one character assigned.
 */
export function buildActorScenePresence(
    characters: Character[],
    personnel: Personnel[],
    castAssignments?: CastAssignment[]
): Map<string, Set<string>> {
    const presenceMap = new Map<string, Set<string>>();

    // Build actor→character mapping from CastAssignment records
    const assignments = castAssignments || [];
    const activeCharacters = characters.filter(c => !c.deleted);

    for (const character of activeCharacters) {
        const actorId = getPrimaryActorId(character.id, assignments);
        if (!actorId) continue;
        
        // Verify actor exists in personnel
        if (!personnel.some(p => p.id === actorId && !p.deleted)) continue;

        if (!presenceMap.has(actorId)) {
            presenceMap.set(actorId, new Set<string>());
        }

        const sceneSet = presenceMap.get(actorId)!;
        for (const sceneId of character.sceneIds) {
            sceneSet.add(sceneId);
        }
    }

    return presenceMap;
}

/**
 * Sorts actor IDs by their first scene appearance in show order.
 * Actors who appear earlier in the show come first.
 * Actors with no scene presence are sorted to the end.
 */
export function sortActorsByAppearance(
    actorIds: string[],
    actorScenePresence: Map<string, Set<string>>,
    sceneOrder: string[]
): string[] {
    return [...actorIds].toSorted((a, b) => {
        const scenesA = actorScenePresence.get(a);
        const scenesB = actorScenePresence.get(b);

        const firstA = scenesA
            ? sceneOrder.findIndex(s => scenesA.has(s))
            : Number.POSITIVE_INFINITY;
        const firstB = scenesB
            ? sceneOrder.findIndex(s => scenesB.has(s))
            : Number.POSITIVE_INFINITY;

        // -1 from findIndex means not found, treat as Infinity
        const indexA = firstA === -1 ? Number.POSITIVE_INFINITY : firstA;
        const indexB = firstB === -1 ? Number.POSITIVE_INFINITY : firstB;

        return indexA - indexB;
    });
}

/**
 * Generates swap suggestions when there are more actors than packs.
 * 
 * For each un-packed actor, finds an unlocked pack whose current owner
 * has no overlapping scenes with the un-packed actor. Prefers the pack
 * with the longest continuous offstage gap around the un-packed actor's scenes.
 * 
 * @returns swaps - MicSwap entries at scene boundaries where assignment changes
 * @returns unresolved - actor IDs that couldn't be assigned a swap
 */
export function generateSwapSuggestions(
    assignments: MicAssignment[],
    unpackedActorIds: string[],
    actorScenePresence: Map<string, Set<string>>,
    sceneOrder: string[]
): { swaps: MicSwap[]; unresolved: string[] } {
    const swaps: MicSwap[] = [];
    const unresolved: string[] = [];

    // Only non-locked assignments are candidates for sharing; prefer lowPriority first
    const unlocked = assignments
        .filter(a => a.lockState !== 'locked')
        .toSorted((a, b) => {
            if (a.lockState === 'lowPriority' && b.lockState !== 'lowPriority') return -1;
            if (a.lockState !== 'lowPriority' && b.lockState === 'lowPriority') return 1;
            return 0;
        });

    for (const unpackedActorId of unpackedActorIds) {
        const unpackedScenes = actorScenePresence.get(unpackedActorId);
        if (!unpackedScenes || unpackedScenes.size === 0) {
            unresolved.push(unpackedActorId);
            continue;
        }

        // Find the best swap candidate: unlocked pack whose owner doesn't overlap
        let bestCandidate: MicAssignment | null = null;
        let bestGapLength = -1;

        for (const candidate of unlocked) {
            const candidateScenes = actorScenePresence.get(candidate.actorId);
            if (!candidateScenes) continue;

            // Check for scene overlap
            let hasOverlap = false;
            for (const sceneId of unpackedScenes) {
                if (candidateScenes.has(sceneId)) {
                    hasOverlap = true;
                    break;
                }
            }

            if (hasOverlap) continue;

            // Calculate the gap length — how many scenes the candidate is offstage
            // while the un-packed actor needs the mic
            let gapLength = 0;
            for (const sceneId of sceneOrder) {
                if (unpackedScenes.has(sceneId) && !candidateScenes.has(sceneId)) {
                    gapLength++;
                }
            }

            if (gapLength > bestGapLength) {
                bestGapLength = gapLength;
                bestCandidate = candidate;
            }
        }

        if (!bestCandidate) {
            unresolved.push(unpackedActorId);
            continue;
        }

        // Generate swap entries at the scene boundaries
        const candidateScenes = actorScenePresence.get(bestCandidate.actorId)!;
        let currentHolder = bestCandidate.actorId;
        
        for (let i = 0; i < sceneOrder.length; i++) {
            const sceneId = sceneOrder[i]!;
            const unpackedNeedsIt = unpackedScenes.has(sceneId);
            const candidateNeedsIt = candidateScenes.has(sceneId);
            
            // Determine who should hold the pack for this scene
            let nextHolder: string;
            if (candidateNeedsIt) {
                nextHolder = bestCandidate.actorId;
            } else if (unpackedNeedsIt) {
                nextHolder = unpackedActorId;
            } else {
                // Neither needs it — keep current holder
                nextHolder = currentHolder;
            }

            // If holder changes, create a swap entry
            if (nextHolder !== currentHolder && i > 0) {
                swaps.push({
                    id: crypto.randomUUID(),
                    packNumber: bestCandidate.packNumber,
                    fromActorId: currentHolder,
                    toActorId: nextHolder,
                    afterSceneId: sceneOrder[i - 1]!, // Swap happens after the previous scene
                    isAutoSuggested: true,
                    isConfirmed: false,
                });
            }

            currentHolder = nextHolder;
        }
    }

    return { swaps, unresolved };
}

/**
 * Calculates the minimum number of mic packs required.
 *
 * Returns two values:
 * - **absolute**: Peak mic-eligible actors in any single scene (ignoring locks — for rental budgeting)
 * - **practical**: locked_count + peak *non-locked* actors in any single scene (respecting current locks)
 *
 * When no actors are locked, practical === absolute.
 */
export function calculateMinimumPacks(
    actorScenePresence: Map<string, Set<string>>,
    sceneOrder: string[],
    lockedActorIds: Set<string> = new Set()
): { absolute: number; practical: number } {
    if (sceneOrder.length === 0 || actorScenePresence.size === 0) {
        return { absolute: 0, practical: 0 };
    }

    let peakAll = 0;
    let peakNonLocked = 0;

    for (const sceneId of sceneOrder) {
        let countAll = 0;
        let countNonLocked = 0;

        for (const [actorId, scenes] of actorScenePresence) {
            if (scenes.has(sceneId)) {
                countAll++;
                if (!lockedActorIds.has(actorId)) {
                    countNonLocked++;
                }
            }
        }

        if (countAll > peakAll) peakAll = countAll;
        if (countNonLocked > peakNonLocked) peakNonLocked = countNonLocked;
    }

    return {
        absolute: peakAll,
        practical: lockedActorIds.size + peakNonLocked,
    };
}
